import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { ModalFormContainerComponent } from '../../ui/modal-form-container/modal-form-container.component';
import { IContact, IOrganization } from '../../../types/contactModule';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { GroupPickerComponent } from '../../organization/contacts/group-picker/group-picker.component';
import { formatCount } from '../../../../utils/sms';

/** Adds saved contacts to one or more organizations (bulk action on contact lists). */
@Component({
  selector: 'assign-multiple-contacts-to-organizations',
  standalone: true,
  imports: [ModalFormContainerComponent, GroupPickerComponent],
  templateUrl: './assign-multiple-contacts-to-organizations.component.html',
  styleUrls: [
    '../../organization/contacts/group-picker/group-modal.css',
    './assign-multiple-contacts-to-organizations.component.css',
  ],
})
export class AssignMultipleContactsToOrganizationsComponent implements OnChanges {
  @Input({ required: true }) open: boolean = false;
  @Input() selectedContacts: IContact[] = [];
  @Input() selection: IOrganization[] = [];
  @Output() resetSelectedContacts = new EventEmitter();
  @Output() close = new EventEmitter();

  // services
  contactService = inject(ContactsService);

  selectedOrganizations: IOrganization[] = [];
  showModal: boolean = false;
  saving = false;
  errorMsg = '';

  get contactLabel(): string {
    const count = this.selectedContacts?.length ?? 0;
    return count === 1 ? '1 contact' : `${formatCount(count)} contacts`;
  }

  get submitLabel(): string {
    const count = this.selectedOrganizations.length;
    if (this.saving) return 'Adding…';
    if (!count) return 'Choose organizations';
    return count === 1 ? 'Add to 1 organization' : `Add to ${count} organizations`;
  }

  updateSelection(items: any[]) {
    this.selectedOrganizations = items as IOrganization[];
    this.errorMsg = '';
  }

  handleSubmit() {
    if (!this.selectedOrganizations.length || this.saving) return;
    this.saving = true;
    this.errorMsg = '';
    const orgCount = this.selectedOrganizations.length;
    const label = this.contactLabel;
    this.contactService
      .assignMultipleContactsToOrganizations(
        this.selectedContacts.map((el) => el.uuid as string),
        this.selectedOrganizations.map((el) => el.uuid as string)
      )
      .subscribe({
        next: () => {
          this.saving = false;
          this.contactService.showSuccess(
            `Added ${label} to ${orgCount === 1 ? '1 organization' : `${orgCount} organizations`}.`
          );
          this.resetSelectedContacts.emit();
          this.closeModal();
        },
        error: () => {
          this.saving = false;
          this.errorMsg = 'The contacts were not added. Check your connection and try again.';
        },
      });
  }

  closeModal() {
    this.close.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['open'] || changes['selection']) {
      // A fresh copy each time it opens, so earlier ticks never leak back in.
      this.selectedOrganizations = [...(this.selection ?? [])];
      this.errorMsg = '';
      this.saving = false;
    }
    this.showModal = this.open;
  }
}
